/**
 * useInvoiceFormEffects – helpers
 *
 * Async fetchers and pure transforms extracted from useInvoiceFormEffects.ts
 * so each effect stays simple and the main hook stays under the
 * max-lines-per-function / complexity limits.
 */

import type { Address } from '@/gen/proto'

import { listAccounts, type Account } from '../../../services/account-service'
import { listBankAccounts, type BankAccount } from '../../../services/bank-account-service'
import { getEntity } from '../../../services/entity-service'
import { listInvoices } from '../../../services/invoice-service'
import { getOrganization } from '../../../services/organization-service'
import { listRoleDefinitions } from '../../../services/role-definitions-service'
import { ROLE_CUSTOMER, ROLE_SUPPLIER } from '../../../types/billing/party-types'
import { addressHasUsage } from '../../../utils/billing-helpers'

// ─────────────────────────────────────────────────────────────────────────────
// Bank accounts
// ─────────────────────────────────────────────────────────────────────────────

export interface BankAccountsFetchResult {
  bankOnly: BankAccount[]
  glBankAccounts: Account[]
  cashGLAccounts: Account[]
}

export async function fetchBankAccountsRaw(
  orgId: string,
  ledgerId: string,
): Promise<BankAccountsFetchResult> {
  const [bankRes, assetRes] = await Promise.all([
    listBankAccounts(orgId, { ledgerId, activeOnly: true }),
    listAccounts(orgId, { accountType: 'ACCOUNT_TYPE_ASSET', includeHeaders: false }, 200),
  ])

  const bankOnly = (bankRes.data?.bankAccounts ?? []).filter((acc) => acc.accountType !== 'cash')
  const linkedAccountIds = new Set(bankOnly.map(b => b.accountId))
  const allAssets = assetRes.data?.accounts ?? []

  const glBankAccounts = allAssets.filter(
    a => a.isActive && !a.isHeader && /bank/i.test(a.accountSubtype) && !linkedAccountIds.has(a.id),
  )
  const cashGLAccounts = allAssets.filter(
    a => a.isActive && !a.isHeader && (/bank/i.test(a.accountSubtype) || /cash/i.test(a.accountSubtype)),
  )

  return { bankOnly, glBankAccounts, cashGLAccounts }
}

export function pickInitialBankSelection(args: {
  bankOnly: BankAccount[]
  glBankAccounts: { id: string }[]
  initialBankDetails?: { accountNumber?: string; bankName?: string; accountName?: string } | null
  selectedBankAccountId: string | null
}): string | null {
  const { bankOnly, glBankAccounts, initialBankDetails, selectedBankAccountId } = args
  if (selectedBankAccountId) { return null }

  if (initialBankDetails) {
    const matching = bankOnly.find(
      (acc) =>
        acc.accountNumber === initialBankDetails.accountNumber ||
        (acc.bankName === initialBankDetails.bankName && acc.accountName === initialBankDetails.accountName),
    )
    if (matching) { return `bank:${matching.id}` }
  }

  const defaultAccount = bankOnly.find((acc) => acc.isDefault)
  if (defaultAccount) { return `bank:${defaultAccount.id}` }
  if (bankOnly.length > 0 && bankOnly[0]?.id) { return `bank:${bankOnly[0].id}` }
  if (glBankAccounts.length > 0 && glBankAccounts[0]?.id) { return `gl:${glBankAccounts[0].id}` }
  return null
}

// ─────────────────────────────────────────────────────────────────────────────
// Entity addresses normalization
// ─────────────────────────────────────────────────────────────────────────────

const KNOWN_PURPOSES = ['billing', 'shipping', 'consignee']

function deriveAddressUsage(addr: { usage?: string[]; type?: string; label?: string }): string[] {
  const storedUsage = addr.usage ?? []
  if (storedUsage.length > 0) { return storedUsage }
  if (KNOWN_PURPOSES.includes(addr.type ?? '')) { return [addr.type ?? 'billing'] }
  if (KNOWN_PURPOSES.includes(addr.label ?? '')) { return [addr.label ?? 'billing'] }
  return ['billing']
}

type AddrInput = Partial<Address> & { usage?: string[]; companyCode?: string }

const STRING_FIELDS = [
  'label', 'type', 'line1', 'line2', 'city',
  'state', 'stateCode', 'postalCode', 'country', 'gstin', 'companyCode',
] as const

function emptyStringFields(addr: AddrInput): Record<(typeof STRING_FIELDS)[number], string> {
  const result = {} as Record<(typeof STRING_FIELDS)[number], string>
  for (const key of STRING_FIELDS) {
    result[key] = ((addr as Record<string, unknown>)[key] as string | undefined) ?? ''
  }
  return result
}

function normalizeEntityAddress(
  addr: AddrInput,
  entityId: string,
  index: number,
): Address {
  return {
    id: addr.id ?? `${entityId}-${addr.label ?? index}`,
    ...emptyStringFields(addr),
    usage: deriveAddressUsage(addr),
    countryCode: addr.countryCode ?? 'IN',
    isPrimary: addr.isPrimary ?? false,
  } as Address
}

export async function fetchAndNormalizeEntityAddresses(entityId: string): Promise<Address[]> {
  const entityResponse = await getEntity(entityId)
  if (!entityResponse.data?.addresses) { return [] }
  return entityResponse.data.addresses.map((addr, index) =>
    normalizeEntityAddress(addr as Parameters<typeof normalizeEntityAddress>[0], entityId, index),
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Last invoice address auto-fill
// ─────────────────────────────────────────────────────────────────────────────

export function buildAddressFromSnapshot(
  snapshot: Partial<Address>,
  type: string,
  id: string,
): Address {
  return {
    id,
    label: 'From Last Invoice',
    type,
    line1: snapshot.line1 ?? '',
    line2: snapshot.line2 ?? '',
    city: snapshot.city ?? '',
    state: snapshot.state ?? '',
    stateCode: snapshot.stateCode ?? '',
    postalCode: snapshot.postalCode ?? '',
    country: snapshot.country ?? '',
    countryCode: snapshot.countryCode ?? '',
    gstin: snapshot.gstin ?? '',
    companyCode: '',
    isPrimary: false,
  }
}

export interface FetchLastInvoiceContext {
  orgId: string
  ledgerId: string
  partyId: string
}

export async function fetchLastInvoiceForParty(
  ctx: FetchLastInvoiceContext,
): Promise<{ shipToAddress?: Partial<Address> | null; consigneeAddress?: Partial<Address> | null; id?: string } | null> {
  const response = await listInvoices(
    ctx.orgId, ctx.ledgerId,
    { partyId: ctx.partyId },
    { pageSize: 1 },
  )
  if (!response.data?.invoices || response.data.invoices.length === 0) { return null }
  return response.data.invoices[0] ?? null
}

export interface AutoFillExportAddressesArgs {
  allPartyAddresses: Address[]
  setSelectedShipToAddress: (a: Address | null) => void
  setSelectedConsigneeAddress: (a: Address | null) => void
  setShipToType: (t: 'party' | 'same' | 'custom') => void
  setConsigneeType: (t: 'party' | 'same' | 'custom') => void
}

export function autoFillExportAddressesFromParty(args: AutoFillExportAddressesArgs): void {
  const {
    allPartyAddresses, setSelectedShipToAddress, setSelectedConsigneeAddress,
    setShipToType, setConsigneeType,
  } = args
  if (allPartyAddresses.length === 0) { return }
  const shippingAddr = allPartyAddresses.find((addr) => addressHasUsage(addr, 'shipping') && addr.isPrimary)
    ?? allPartyAddresses.find((addr) => addressHasUsage(addr, 'shipping'))
  if (shippingAddr) {
    setSelectedShipToAddress(shippingAddr)
    setShipToType('party')
  }
  const consigneeAddr = allPartyAddresses.find((addr) => addressHasUsage(addr, 'consignee') && addr.isPrimary)
    ?? allPartyAddresses.find((addr) => addressHasUsage(addr, 'consignee'))
  if (consigneeAddr) {
    setSelectedConsigneeAddress(consigneeAddr)
    setConsigneeType('party')
  }
}

export function autoFillExportShipToFromParty(args: AutoFillExportAddressesArgs): void {
  const { allPartyAddresses, setSelectedShipToAddress, setShipToType } = args
  if (allPartyAddresses.length === 0) { return }
  const shippingAddr = allPartyAddresses.find((addr) => addressHasUsage(addr, 'shipping') && addr.isPrimary)
    ?? allPartyAddresses.find((addr) => addressHasUsage(addr, 'shipping'))
  if (shippingAddr) {
    setSelectedShipToAddress(shippingAddr)
    setShipToType('party')
  }
}

export interface ApplyLastInvoiceAddressesArgs {
  lastInvoice: { shipToAddress?: Partial<Address> | null; consigneeAddress?: Partial<Address> | null; id?: string }
  invoiceType: string
  setSelectedShipToAddress: (a: Address | null) => void
  setSelectedConsigneeAddress: (a: Address | null) => void
  setShipToType: (t: 'party' | 'same' | 'custom') => void
  setConsigneeType: (t: 'party' | 'same' | 'custom') => void
}

export function applyLastInvoiceAddresses(args: ApplyLastInvoiceAddressesArgs): void {
  const { lastInvoice, invoiceType, setSelectedShipToAddress, setSelectedConsigneeAddress, setShipToType, setConsigneeType } = args
  if (invoiceType !== 'export') { return }
  if (lastInvoice.shipToAddress) {
    setSelectedShipToAddress(buildAddressFromSnapshot(lastInvoice.shipToAddress, 'shipping', `last-invoice-${lastInvoice.id}`))
    setShipToType('party')
  }
  if (lastInvoice.consigneeAddress) {
    setSelectedConsigneeAddress(buildAddressFromSnapshot(lastInvoice.consigneeAddress, 'consignee', `last-invoice-consignee-${lastInvoice.id}`))
    setConsigneeType('party')
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Party role options (Feature: party-role-based account selection)
// ─────────────────────────────────────────────────────────────────────────────

export interface PartyRoleOptionsResult {
  options: Array<{ value: string; label: string }>
  defaultRole: string
}

/**
 * Loads the active party-role definitions for orgId, filtered to roles whose
 * billing direction supports this document (isVendor -> canIssueInvoices,
 * else canReceiveInvoices), plus the org's default role for that direction.
 * defaultRole falls back to the built-in customer/supplier role when the org
 * has no default set, or its configured default isn't (or is no longer) a
 * valid option for this direction.
 */
export async function loadPartyRoleOptions(orgId: string, isVendor: boolean): Promise<PartyRoleOptionsResult> {
  const [rolesRes, orgRes] = await Promise.all([
    listRoleDefinitions(orgId),
    getOrganization(orgId),
  ])
  const builtIn = isVendor ? ROLE_SUPPLIER : ROLE_CUSTOMER
  const roles = (rolesRes.data ?? []).filter(
    (r) => r.isActive && (isVendor ? r.billing.canIssueInvoices : r.billing.canReceiveInvoices),
  )
  const options = roles.map((r) => ({ value: r.role, label: r.label }))
  if (options.length === 0) { return { options: [], defaultRole: '' } }

  const orgDefault = isVendor ? orgRes.data?.defaultPurchaseRole : orgRes.data?.defaultSalesRole
  const defaultRole = pickDefaultRole(options, orgDefault, builtIn)

  return { options, defaultRole }
}

function pickDefaultRole(
  options: Array<{ value: string; label: string }>,
  orgDefault: string | undefined,
  builtIn: string,
): string {
  if (orgDefault && options.some((o) => o.value === orgDefault)) { return orgDefault }
  if (options.some((o) => o.value === builtIn)) { return builtIn }
  return options[0]?.value ?? ''
}
